import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import '../styles/AdminBurn.css';

// Contract addresses
const burnManagerAddress = "0x7231CC09c3A4236163E6BC05c1A848ACE397408B";
const tokenAddress = "0x62217F8c102A33b6112C8B5B897BB9ee400737a5"; // Replace with your token address
const adminAddress = "0x3A6163B029081e1C4b94CC98f2A6895CA314D76a";

const contractABI = [
  // BurningManager functions
  "function manualBurn(uint256 amount, string calldata pool, string calldata reason)",
  "function dynamicBurnFromPool(uint256 amount, string calldata pool, string calldata reason)",
  "function burnFromPool(string calldata pool, uint256 amount, string calldata reason)",
  "function scheduleBurn(uint256 amount, uint256 executeAfter, string calldata pool, string calldata reason)",
  "function getAllPools() view returns (string[] memory)",
  
  // Token functions
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

const AdminBurn = () => {
  const [burnType, setBurnType] = useState("Manual");
  const [amount, setAmount] = useState("");
  const [pool, setPool] = useState("");
  const [reason, setReason] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [currentAllowance, setCurrentAllowance] = useState("0");

  useEffect(() => {
    const initialize = async () => {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        const userAddress = await signer.getAddress();
        
        if (userAddress.toLowerCase() === adminAddress.toLowerCase()) {
          setIsAdmin(true);
          const contract = new ethers.Contract(burnManagerAddress, contractABI, provider);
          
          // Load pools
          const poolList = await contract.getAllPools();
          setPools(poolList);
          if (poolList.length > 0) setPool(poolList[0]);
          
          // Load current allowance
          const tokenContract = new ethers.Contract(tokenAddress, contractABI, provider);
          const allowance = await tokenContract.allowance(adminAddress, burnManagerAddress);
          setCurrentAllowance(ethers.utils.formatUnits(allowance, 18));
        }
      } catch (error) {
        console.error("Initialization error:", error);
        setStatusMessage(`❌ Initialization error: ${error.message}`);
      }
    };
    
    initialize();
  }, []);

  const executeBurn = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0 || !reason || !pool) {
      setStatusMessage("❗ Please fill all fields with valid values.");
      return;
    }

    try {
      setLoading(true);
      setStatusMessage("");

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = provider.getSigner();
      const userAddress = await signer.getAddress();

      if (userAddress.toLowerCase() !== adminAddress.toLowerCase()) {
        throw new Error("You are not authorized to perform this action.");
      }

      const amountInWei = ethers.utils.parseUnits(amount, 18);
      const contract = new ethers.Contract(burnManagerAddress, contractABI, signer);
      const tokenContract = new ethers.Contract(tokenAddress, contractABI, signer);

      // For Wallet burns (needs approval)
      if (burnType === "Wallet") {
        setStatusMessage("⏳ Checking token allowance...");
        
        // Check current allowance
        const allowance = await tokenContract.allowance(adminAddress, burnManagerAddress);
        
        if (allowance.lt(amountInWei)) {
          setStatusMessage("⏳ Approving tokens...");
          const approveTx = await tokenContract.approve(
            burnManagerAddress,
            amountInWei,
            { gasLimit: 100000 }
          );
          await approveTx.wait();
          setCurrentAllowance(amount);
        }

        setStatusMessage("⏳ Executing burn...");
        const tx = await contract.burnFromPool(
          pool,
          amountInWei,
          reason,
          { gasLimit: 250000 }
        );
        await tx.wait();
      } 
      // For other burn types
      else {
        setStatusMessage("⏳ Processing burn...");
        let tx;
        
        const txOptions = { gasLimit: 250000 };
        
        if (burnType === "Manual") {
          tx = await contract.manualBurn(amountInWei, pool, reason, txOptions);
        } 
        else if (burnType === "Dynamic") {
          tx = await contract.dynamicBurnFromPool(amountInWei, pool, reason, txOptions);
        } 
        else if (burnType === "Scheduled") {
          if (!scheduleTime) throw new Error("Please select a schedule time.");
          const timestamp = Math.floor(new Date(scheduleTime).getTime() / 1000);
          tx = await contract.scheduleBurn(amountInWei, timestamp, pool, reason, txOptions);
        }
        
        await tx.wait();
      }

      setStatusMessage("✅ Burn executed successfully!");
      setAmount("");
      setReason("");
      setScheduleTime("");
      
      // Refresh allowance
      const newAllowance = await tokenContract.allowance(adminAddress, burnManagerAddress);
      setCurrentAllowance(ethers.utils.formatUnits(newAllowance, 18));
    } catch (err) {
      console.error("Burn error:", err);
      const errorMessage = err.reason 
        ? err.reason.replace("execution reverted: ", "") 
        : err.message;
      setStatusMessage(`❌ Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-burn-container">
        <h2>🔥 Admin Burn Dashboard</h2>
        <p className="auth-error">You are not authorized to access this page.</p>
      </div>
    );
  }

  return (
    <div className="admin-burn-container">
      <h2>🔥 Admin Burn Dashboard</h2>
      
      <div className="burn-info">
        <p>Current Allowance: {currentAllowance} MCN</p>
      </div>

      {statusMessage && (
        <div className={`status-banner ${statusMessage.includes("✅") ? "success" : "error"}`}>
          {statusMessage}
        </div>
      )}

      <div className="admin-burn-form">
        <div className="form-group">
          <label>Amount (MCN)</label>
          <input
            type="number"
            placeholder="e.g. 1000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0"
            step="0.1"
          />
        </div>

        <div className="form-group">
          <label>Pool</label>
          <select value={pool} onChange={(e) => setPool(e.target.value)}>
            {pools.map((p, index) => (
              <option key={index} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Burn Type</label>
          <select value={burnType} onChange={(e) => setBurnType(e.target.value)}>
            <option value="Manual">Manual Burn (from Contract)</option>
            <option value="Wallet">Wallet Burn (Pull then Burn)</option>
            <option value="Dynamic">Dynamic Burn (After Receiving)</option>
            <option value="Scheduled">Scheduled Burn</option>
          </select>
        </div>

        {burnType === "Scheduled" && (
          <div className="form-group">
            <label>Schedule Time</label>
            <input
              type="datetime-local"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>
        )}

        <div className="form-group">
          <label>Reason</label>
          <input
            type="text"
            placeholder="Describe the burn purpose"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <button 
          onClick={executeBurn} 
          disabled={loading}
          className={loading ? "loading" : ""}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Processing...
            </>
          ) : (
            "🔥 Execute Burn"
          )}
        </button>
      </div>
    </div>
  );
};

export default AdminBurn;